import { UserSettingsProjectState } from '@models/user-settings';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import PushPinRounded from '@mui/icons-material/PushPinRounded';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { typedMemo } from '@utils';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProjectMenuItem.module.scss';

interface ProjectMenuItemProps {
  project: UserSettingsProjectState;
  onProjectSelect: (project: UserSettingsProjectState) => void;
  onProjectPin: (project: UserSettingsProjectState, shouldPin: boolean) => void;
  isPinned: boolean;
  canPin: boolean;
  listType: string;
  isCurrent?: boolean;
}

export const ProjectMenuItem = typedMemo(
  ({
    project,
    onProjectSelect,
    onProjectPin,
    isPinned,
    canPin,
    listType,
    isCurrent = false,
  }: Readonly<ProjectMenuItemProps>) => {
    const { t } = useTranslation();

    const getTooltipTitle = () => {
      if (isPinned) return t('projectSelector.unpinProject');
      return canPin
        ? t('projectSelector.pinProject')
        : t('projectSelector.maxPinnedReached');
    };

    const secondLineParts = [project.nummer, project.stadiumname].filter(
      Boolean,
    );
    const secondLineText = secondLineParts.join(' | ');
    const fullTitle = `${project.kurzname} ${secondLineText}`;
    const shouldHidePinButton = listType === 'recent' && isPinned;
    const firstLineRef = useRef<HTMLSpanElement>(null);
    const secondLineRef = useRef<HTMLSpanElement>(null);

    const isTruncated =
      (firstLineRef.current &&
        firstLineRef.current.scrollWidth > firstLineRef.current.clientWidth) ||
      (secondLineRef.current &&
        secondLineRef.current.scrollWidth > secondLineRef.current.clientWidth);

    const handlePinClick = useCallback(
      () => onProjectPin(project, !isPinned),
      [onProjectPin, project, isPinned],
    );

    const handleSelectClick = useCallback(
      () => onProjectSelect(project),
      [onProjectSelect, project],
    );

    return (
      <div className={styles.projectItemContainer}>
        <div className={styles.pinIcon}>
          {shouldHidePinButton ? (
            <div className={styles.pinPlaceholder} />
          ) : (
            <Tooltip
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
              disableInteractive
              title={getTooltipTitle()}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handlePinClick}
                  disabled={!isPinned && !canPin}
                  className={styles.pinButton}
                  data-testid="project-item-pin-button"
                >
                  <div style={{ position: 'relative', width: 20, height: 20 }}>
                    <Fade in={!isPinned} timeout={300}>
                      <PushPinOutlined
                        fontSize="small"
                        color={canPin ? 'action' : 'disabled'}
                        style={{ position: 'absolute', top: 0, left: 0 }}
                      />
                    </Fade>
                    <Fade in={isPinned} timeout={300}>
                      <PushPinRounded
                        fontSize="small"
                        color="primary"
                        style={{ position: 'absolute', top: 0, left: 0 }}
                      />
                    </Fade>
                  </div>
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>

        <Tooltip
          title={isTruncated ? fullTitle : ''}
          key={project.projektstadiumId}
          placement="top"
          disableInteractive
          enterDelay={500}
          enterNextDelay={500}
          slotProps={{
            popper: {
              modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
            },
          }}
        >
          <MenuItem
            onClick={handleSelectClick}
            className={styles.projectItem}
            data-testid={`project-item-${listType}`}
            disabled={isCurrent}
          >
            <div>
              <Typography
                ref={firstLineRef}
                data-testid={`project-item-title-${listType}`}
                className={styles.truncatedText}
              >
                {project.kurzname}
              </Typography>
              <Typography
                ref={secondLineRef}
                data-testid={`project-item-title2-${listType}`}
                className={styles.truncatedText}
              >
                {secondLineText}
              </Typography>
            </div>
          </MenuItem>
        </Tooltip>
      </div>
    );
  },
);
